// src/lib/calc.js — Rekenkern volgens Handleiding toepassing zzp-starttarieven (stap 5)
// Openingsbod = totaal uren (hoofdwerk + voorbereiding + afronding [+ pr/promotie]) × starttarief.
// Onregelmatige uren, complexiteit en ervaring zijn onderhandelpunten — niet in de berekening.
//
// Festival-flow (bucket F): rekent in werkdagen (zzp) of bruto maandsalaris (loondienst).
// Dagprijs = uurtarief × 7,6 (excl. pauze, conform basisregeling SAV).

import { config } from "./config";

const FESTIVAL_DAG_UREN = 7.6;

export const calc = {
  tariefFilmAV(niveau) {
    return config.tarieven_film_av?.find(t => t.niveau === niveau);
  },

  tariefFestival(niveau) {
    return config.tarieven_festival?.find(t => t.niveau === niveau);
  },

  tariefFestivalLoondienst(niveau, periodiek) {
    const p = String(periodiek);
    return config.tarieven_festival_loondienst?.find(
      t => t.niveau === niveau && String(t.periodiek) === p,
    );
  },

  /**
   * Bereken openingsbod voor één rol-regel.
   * @param {object} rol - { rol, dep, niveau, uren: { hoofdwerk, voorbereiding, afronding, pr_promotie? } }
   * @param {string} type - "film" | "festival"
   */
  berekenRol(rol, type) {
    const tariefRij = type === "festival"
      ? this.tariefFestival(rol.niveau)
      : this.tariefFilmAV(rol.niveau);
    if (!tariefRij) return null;

    const uurtarief = tariefRij.tarief_min;

    const uren = rol.uren || {};
    const urenHoofdwerk     = Number(uren.hoofdwerk)     || 0;
    const urenVoorbereiding = Number(uren.voorbereiding) || 0;
    const urenAfronding     = Number(uren.afronding)     || 0;
    const urenPrPromotie    = Number(uren.pr_promotie)   || 0;
    const urenTotaal = urenHoofdwerk + urenVoorbereiding + urenAfronding + urenPrPromotie;

    const openingsbod = urenTotaal * uurtarief;

    return {
      tariefRij,
      uurtarief,
      urenHoofdwerk,
      urenVoorbereiding,
      urenAfronding,
      urenPrPromotie,
      urenTotaal,
      openingsbod,
    };
  },

  /**
   * Bereken opdracht-totaal (som van alle rol-regels).
   * @param {object} state - { type, rollen: [...], btw21 }
   */
  berekenOpdracht(state) {
    const rollen = Array.isArray(state.rollen) ? state.rollen : [];
    const rolResultaten = rollen.map(r => ({
      rol: r,
      result: this.berekenRol(r, state.type),
    }));

    const subtotaalArbeid = rolResultaten.reduce(
      (sum, r) => sum + (r.result?.openingsbod || 0), 0
    );

    const totaalExBtw = subtotaalArbeid;
    const btwPct = Number(config.generiek?.btw_pct);
    const btw = state.btw21 ? totaalExBtw * btwPct : 0;
    const totaalInclBtw = totaalExBtw + btw;

    return {
      rolResultaten,
      subtotaalArbeid,
      totaalExBtw,
      btw,
      totaalInclBtw,
    };
  },

  /**
   * Festival-zzp openingsbod (bucket F flow F-ZZP).
   * Dagtarief = uurtarief × 7,6. Openingsbod = werkdagen × dagtarief.
   * `festivaldagen` is informatief (onderhandelpunt SAV), telt niet apart in het bedrag.
   * @param {object} rol - { niveau, werkdagen: { totaal, festivaldagen } }
   */
  berekenFestivalZzp(rol) {
    const tariefRij = this.tariefFestival(rol.niveau);
    if (!tariefRij) return null;
    const uurtarief = Number(tariefRij.tarief_min) || 0;
    const dagtarief = uurtarief * FESTIVAL_DAG_UREN;
    const wd = rol.werkdagen || {};
    const werkdagenTotaal = Number(wd.totaal) || 0;
    const festivaldagen = Number(wd.festivaldagen) || 0;
    const openingsbod = werkdagenTotaal * dagtarief;
    return { tariefRij, uurtarief, dagtarief, werkdagenTotaal, festivaldagen, openingsbod };
  },

  /**
   * Festival-loondienst (bucket F flow F-LD).
   * Per dienstverband-blok bruto/maand = bruto_fulltime × pct/100.
   * Gemiddeld bruto/maand = totaalBruto / totaalMaanden (gewogen op maanden).
   * @param {object} rol - { niveau, periodiek, dienstverband: [{ maanden, pct }] }
   */
  berekenFestivalLoondienst(rol) {
    const periodiek = rol.periodiek != null ? rol.periodiek : 0;
    const tariefRij = this.tariefFestivalLoondienst(rol.niveau, periodiek);
    if (!tariefRij) return null;
    const brutoFulltime = Number(tariefRij.bruto_fulltime) || 0;
    const blokken = Array.isArray(rol.dienstverband) ? rol.dienstverband : [];
    const perBlok = blokken.map(b => {
      const maanden = Math.max(0, Number(b.maanden) || 0);
      const pct = Math.max(0, Math.min(100, Number(b.pct) || 0));
      const brutoMaand = brutoFulltime * (pct / 100);
      return { ...b, maanden, pct, brutoMaand, brutoBlok: brutoMaand * maanden };
    });
    const totaalMaanden = perBlok.reduce((s, b) => s + b.maanden, 0);
    const totaalBruto = perBlok.reduce((s, b) => s + b.brutoBlok, 0);
    const gemBruto = totaalMaanden > 0 ? totaalBruto / totaalMaanden : 0;
    return {
      tariefRij, brutoFulltime, periodiek,
      perBlok, totaalMaanden, totaalBruto, gemBruto,
    };
  },

  fmt(n) {
    if (n == null || isNaN(n)) return "—";
    return "€ " + n.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  },
  fmtInt(n) {
    if (n == null || isNaN(n)) return "—";
    return "€ " + Math.round(n).toLocaleString("nl-NL");
  },
};
